import React, { FC, ChangeEvent, useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    TextField,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormLabel,
    Alert,
    LinearProgress,
    Autocomplete,
} from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import NumberOnlyTextField2 from 'components/number-only-text-field/number-only-text-field-2';
import ClinicFilter from 'components/shared/clinic-filter.component';
import { InvoiceNote, Type, InvoiceType } from 'models/invoice';
import { Organization } from 'models/organization';
import MonetaryValueInput from 'components/monetary-value/monetary-value.component';
import { User } from 'models/user';
import { useLazyQuery } from '@apollo/client/react';
import { getDentistsTemplate } from 'apollo/templates/user';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';

type CreateDebitCreditNoteDialogProps = {
    open: boolean;
    onDialogClose: () => void;
    invoiceNote: InvoiceNote;
    onNoteAdd: (invoiceNote: InvoiceNote) => void;
};
const CreateDebitCreditNoteDialog: FC<CreateDebitCreditNoteDialogProps> = ({
    open,
    onDialogClose,
    invoiceNote,
    onNoteAdd,
}: CreateDebitCreditNoteDialogProps) => {
    const { t: translate } = useTranslation(['common', 'invoices', 'orders', 'transport']);
    const [debitCreditNote, setDebitCreditNote] = useState<InvoiceNote>({} as InvoiceNote);
    const [dentists, setDentists] = useState<User[]>([]);
    const [dentist, setDentist] = useState<User | null>(null);
    const [showSpinner, setShowSpinner] = useState<boolean>(false);
    const { showBoundary } = useDenthubErrorBoundary();
    useEffect(() => {
        setDebitCreditNote(invoiceNote);
        setDentist(invoiceNote?.dentist as User);
    }, [invoiceNote]);

    useEffect(() => {
        if (debitCreditNote.clinic) {
            if (debitCreditNote.clinic.invoiceType === InvoiceType.COLLECTIVE_DENTIST) {
                setShowSpinner(true);
                getDentists({
                    variables: {
                        clinicId: debitCreditNote.clinic._id,
                        filters: { active: true },
                        excludeLocalLabOwner: true,
                    },
                });
            }
        }
    }, [debitCreditNote.clinic]);

    const [getDentists] = useLazyQuery(getDentistsTemplate, {
        fetchPolicy: 'network-only',
        onError: (err) => {
            console.error(err, 'err');
            showBoundary(err);
        },
        onCompleted: async ({ dentists }) => {
            setDentists(dentists);
            setShowSpinner(false);
        },
    });

    const validate = (values: InvoiceNote) => {
        const errors: Record<string, string> = {};
        if (!values.type) {
            errors.type = translate('invoices:VALIDATION:TYPE');
        }
        if (!values.description) {
            errors.description = translate('invoices:VALIDATION:LINE_ITEM_DESCRIPTION');
        }
        if (!values.clinic) {
            errors.clinic = translate('invoices:VALIDATION:CLINIC');
        }
        if (values.clinic?.invoiceType === InvoiceType.COLLECTIVE_DENTIST && !values.dentist) {
            errors.clinic = translate('invoices:VALIDATION:DENTIST');
        }
        if (!Number.isInteger(parseInt(values.quantity.toString()))) {
            errors.quantity = translate('invoices:VALIDATION:LINE_ITEM_QUANTITY');
        } else if (values.quantity <= 0) {
            errors.quantity = translate('invoices:VALIDATION:LINE_ITEM_QUANTITY_MIN');
        }
        if (!Number.isInteger(parseInt(values.itemPrice.toString()))) {
            errors.itemPrice = translate('invoices:VALIDATION:LINE_ITEM_PRICE');
        } else if (values.type === Type.CREDIT && values.itemPrice >= 0) {
            errors.itemPrice = translate('invoices:VALIDATION:LINE_ITEM_PRICE');
        }
        return errors;
    };

    const formik = useFormik({
        initialValues: {
            _id: debitCreditNote._id,
            clinic: debitCreditNote.clinic,
            dentist: debitCreditNote.dentist,
            description: debitCreditNote.description,
            quantity: debitCreditNote.quantity || 0,
            itemPrice: debitCreditNote.itemPrice || 0,
            itemVatPercentage: debitCreditNote.itemVatPercentage,
            itemVat: debitCreditNote.itemVat,
            totalExclVat: debitCreditNote.totalExclVat || 0,
            totalIncVat: debitCreditNote.totalIncVat || 0,
            type: debitCreditNote.type,
        },
        validateOnMount: true,
        validate,
        enableReinitialize: true,
        onSubmit: (values: InvoiceNote, { resetForm }) => {
            onNoteAdd({ ...values });
            resetForm();
        },
    });

    const clinicSelectHandler = async (_: React.SyntheticEvent, clinic: Organization | null) => {
        if (clinic) {
            formik.setFieldValue('clinic', clinic);
            setDebitCreditNote({
                ...debitCreditNote,
                clinic: clinic as Organization,
            });
            formik.setFieldValue('dentist', {});
            setDentist(null);
        }
    };

    const handleDentistChange = (_: React.SyntheticEvent, selectedDentist: User) => {
        formik.setFieldValue('dentist', selectedDentist);
        setDentist(selectedDentist);
    };

    const handleQuantityChange = (quantity: number) => {
        formik.setFieldValue('quantity', quantity);

        const totalExclVat = (formik.values.itemPrice || 0) * (Number(quantity) || 0);
        formik.setFieldValue('totalExclVat', totalExclVat);

        const itemVat = (totalExclVat * (Number(formik.values.itemVatPercentage) || 0)) / 100;
        formik.setFieldValue('itemVat', itemVat);

        formik.setFieldValue('totalIncVat', totalExclVat + itemVat);
    };

    const handlePriceChange = (itemPrice: string) => {
        if (Number(itemPrice) < 0) {
            formik.setFieldValue('itemVatPercentage', 0);
        }
        formik.setFieldValue('itemPrice', Number(itemPrice));

        const totalExclVat = (formik.values.quantity || 0) * (Number(itemPrice) || 0);
        formik.setFieldValue('totalExclVat', totalExclVat);

        const itemVat = (totalExclVat * (Number(formik.values.itemVatPercentage) || 0)) / 100;
        formik.setFieldValue('itemVat', itemVat);

        formik.setFieldValue('totalIncVat', totalExclVat + itemVat);
    };

    const handleVatChange = (itemVatPercentage: number) => {
        formik.setFieldValue('itemVatPercentage', itemVatPercentage);

        const totalExclVat = (formik.values.quantity || 0) * (formik.values.itemPrice || 0);

        const itemVat = (totalExclVat * (Number(itemVatPercentage) || 0)) / 100;
        formik.setFieldValue('itemVat', itemVat);

        formik.setFieldValue('totalIncVat', totalExclVat + itemVat);
    };

    const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
        const description = event.target.value;
        if (description.length > 50) {
            event.preventDefault();
            return;
        }
        formik.setFieldValue('description', description);
    };

    const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const noteType = (event.target as HTMLInputElement).value;
        formik.setFieldValue('type', noteType);
        setDebitCreditNote({
            ...debitCreditNote,
            type: noteType as Type,
        });
    };

    return (
        <Dialog maxWidth="md" fullWidth open={open} onClose={onDialogClose}>
            <DialogTitle>
                {translate(`${!debitCreditNote._id ? 'invoices:CREATE_NOTE' : 'invoices:UPDATE_NOTE'}`)}
            </DialogTitle>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <Alert severity="info">{translate('invoices:CREDIT_DEBIT_INFO')}</Alert>
                    <Box mt={2}>
                        <FormControl fullWidth component="fieldset">
                            <FormLabel>{translate('transport:TYPE')}</FormLabel>
                            <RadioGroup
                                row
                                onChange={handleTypeChange}
                                value={formik.getFieldProps('type').value}
                                onBlur={formik.getFieldProps('type').onBlur}
                                name={formik.getFieldProps('type').name}
                            >
                                <FormControlLabel
                                    value="DEBIT"
                                    control={<Radio color="secondary" required />}
                                    label={translate('invoices:DEBIT')}
                                />
                                <FormControlLabel
                                    value="CREDIT"
                                    control={<Radio color="secondary" required />}
                                    label={translate('invoices:CREDIT')}
                                />
                            </RadioGroup>
                            {formik.values.type === Type.CREDIT ? (
                                <Box mt={1}>
                                    <Alert severity="info">{translate('invoices:CREDIT_INFO')}</Alert>
                                </Box>
                            ) : undefined}
                        </FormControl>
                    </Box>
                    <Box mt={2}>
                        <Grid container spacing={1}>
                            <Grid item xs={12}>
                                <ClinicFilter
                                    selectedClinic={debitCreditNote?.clinic}
                                    onClinicFilterChange={clinicSelectHandler}
                                    showParents
                                    onlyCollective
                                    includeCollectiveDentist
                                />
                            </Grid>
                            {debitCreditNote?.clinic?.invoiceType === InvoiceType.COLLECTIVE_DENTIST ? (
                                <Grid item xs={12}>
                                    <Autocomplete
                                        id="dentists-dropdown"
                                        disableClearable
                                        value={dentist as User}
                                        getOptionLabel={(option: User) => option?.fullName || ''}
                                        renderOption={(props, option: User) => (
                                            <li {...{ ...props, key: option?._id }}>{option?.fullName || ''}</li>
                                        )}
                                        isOptionEqualToValue={(option: User, value: User) => option?._id === value?._id}
                                        onChange={handleDentistChange}
                                        options={dentists}
                                        disabled={!debitCreditNote?.clinic?._id || showSpinner}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={translate('common:DENTIST')}
                                                margin="dense"
                                                fullWidth={true}
                                                helperText={formik.touched.dentist && formik.errors.dentist}
                                                error={formik.touched.dentist && Boolean(formik.errors.dentist)}
                                                {...formik.getFieldProps('dentist')}
                                            />
                                        )}
                                    />
                                    {showSpinner ? <LinearProgress /> : undefined}
                                </Grid>
                            ) : undefined}

                            <Grid item xs={12}>
                                <FormControl margin="dense" fullWidth>
                                    <TextField
                                        id="description"
                                        label={translate('common:DESCRIPTION')}
                                        required
                                        helperText={formik.touched.description && formik.errors.description}
                                        error={formik.touched.description && Boolean(formik.errors.description)}
                                        {...formik.getFieldProps('description')}
                                        onChange={handleDescriptionChange}
                                        onBlur={formik.getFieldProps('description').onBlur}
                                        name={formik.getFieldProps('description').name}
                                        value={formik.getFieldProps('description').value}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid item xs={3}>
                                <FormControl margin="dense" fullWidth>
                                    <NumberOnlyTextField2
                                        inputProps={{
                                            style: { textAlign: 'right' },
                                        }}
                                        label={translate('orders:QUANTITY')}
                                        fullWidth
                                        required
                                        hasDecimal={false}
                                        onValueChange={handleQuantityChange}
                                        onBlur={formik.handleBlur}
                                        name={formik.getFieldProps('quantity').name}
                                        value={formik.values.quantity}
                                        helperText={formik.touched.quantity && formik.errors.quantity}
                                        error={formik.touched.quantity && Boolean(formik.errors.quantity)}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={3}>
                                <FormControl margin="dense" fullWidth>
                                    <MonetaryValueInput
                                        inputProps={{
                                            style: { textAlign: 'right' },
                                        }}
                                        label={translate('orders:UNIT_PRICE')}
                                        fullWidth
                                        required
                                        onValueChange={handlePriceChange}
                                        onBlur={formik.handleBlur}
                                        name={formik.getFieldProps('itemPrice').name}
                                        value={formik.getFieldProps('itemPrice').value}
                                        helperText={formik.touched.itemPrice && formik.errors.itemPrice}
                                        error={formik.touched.itemPrice && Boolean(formik.errors.itemPrice)}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={2}>
                                <FormControl margin="dense" fullWidth>
                                    <NumberOnlyTextField2
                                        disabled
                                        inputProps={{
                                            style: { textAlign: 'right' },
                                        }}
                                        label={translate(
                                            `${
                                                formik.values.type !== Type.CREDIT
                                                    ? 'orders:TOTAL'
                                                    : 'invoices:TOTAL_TO_CREDIT'
                                            }`,
                                        )}
                                        fullWidth
                                        value={formik.values.totalExclVat}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={2}>
                                <FormControl margin="dense" fullWidth>
                                    <NumberOnlyTextField2
                                        inputProps={{
                                            style: { textAlign: 'right' },
                                        }}
                                        label={translate('orders:VAT_PERCENT')}
                                        fullWidth
                                        hasDecimal={false}
                                        disabled={formik.values.type === Type.DEBIT && formik.values.itemPrice < 0}
                                        onValueChange={handleVatChange}
                                        onBlur={formik.handleBlur}
                                        name={formik.getFieldProps('itemVatPercentage').name}
                                        value={formik.getFieldProps('itemVatPercentage').value}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item xs={2}>
                                <FormControl margin="dense" fullWidth>
                                    <NumberOnlyTextField2
                                        disabled
                                        inputProps={{
                                            style: { textAlign: 'right' },
                                        }}
                                        label={translate('invoices:TO_PAY_INC_VAT')}
                                        fullWidth
                                        value={formik.values.totalIncVat}
                                    />
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onDialogClose}>{translate('common:CANCEL')}</Button>
                    <Button disabled={!formik.isValid} variant="contained" type="submit">
                        {translate(`${!debitCreditNote._id ? 'common:CREATE' : 'common:UPDATE'}`)}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default CreateDebitCreditNoteDialog;
